import React, {
  useState,
  useEffect,
  useRef,
  MutableRefObject,
  useLayoutEffect,
} from 'react'
import styled from 'styled-components'

import InfiniteList from 'components/InfiniteList'

import { ProjectListItem } from '../ProjectListItem'
import { ProjectLink, ProjectId } from '../../types'

const ListWrapper = styled(InfiniteList)`
  padding: 0;
  margin: 0;
`

interface ProjectListProps {
  items: ProjectLink[]
  onLoadMore: () => Promise<any>
  onDelete: (id: ProjectId, name: string) => void
  onAdd?: () => void
  userCanEdit: boolean
  userCanAdminister: boolean
  workspaceId: string
  refToLast?: HTMLDivElement
  autofocusLastCreated?: MutableRefObject<boolean>
}

/**
 * ProjectsList
 *
 * This component visually show the list of the projects with the options button on hover.
 *
 * @param props
 * @param {TextLink[]} props.items - The list of project items.
 * @param {function} props.onDelete - OnClick handler for the Delete button inside the Dropdown.
 *
 * @example // Basic example
 * <List
 *  items={[{id: '1', name: 'Project 1', link: '/project/1'}]}
 *  onDelete={(id) => { delete(id)}}/>
 */
const ProjectsList: React.FC<ProjectListProps> = props => {
  const {
    items,
    onDelete,
    onLoadMore,
    userCanEdit,
    userCanAdminister,
    workspaceId,
    autofocusLastCreated,
  } = props
  // We are setting the same ref to every project in the list. In this way, is easy to focus
  // in the last one created
  const refToLast = useRef() as React.MutableRefObject<HTMLElement>
  // We want to know that we edited an item
  // and assure that the item has been rendered
  const [editedItem, setEditedItem] = useState('')

  useEffect(() => {
    if (!autofocusLastCreated?.current) return

    refToLast?.current?.focus()
  }, [items.length, autofocusLastCreated])

  useLayoutEffect(() => {
    if (editedItem !== '') {
      refToLast?.current?.focus()
      setEditedItem('')
    }
  }, [editedItem])

  return (
    <ListWrapper onLoadMore={onLoadMore}>
      {items.map(({ link, project }) => (
        <ProjectListItem
          key={project.identifier}
          project={project}
          link={link}
          onDelete={onDelete}
          userCanEdit={userCanEdit}
          userCanAdminister={userCanAdminister}
          workspaceId={workspaceId}
          refToLast={refToLast}
          setEditedItem={setEditedItem}
        />
      ))}
    </ListWrapper>
  )
}

export default ProjectsList
